import PageTransition from "@/components/ui/PageTransition";
import Spinner from "@/components/ui/spinner";
import { Button } from "@/components/ui/button/button";
import Typography from "@/components/ui/typography";
import { useAuth } from "@/context/auth-context";
import useGetBankLinkingSessionStatus from "@/features/bank-accounts/useGetBankLinkingSessionStatus";
import {
	clearBankLinkingSessionFromStorage,
	getBankLinkingSessionFromStorage,
} from "@/utils/bank-linking-session";
import { useLoaderData, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { static_errors } from "@/axios/constants";

const QUERY_DELAY_MS = 2500;

const BankLinkingFailedResponseSchema = z.object({
	error_message: z.string(),
});

const VerifyBankLinkingPage = () => {
	const { location_id } = useLoaderData({ from: "__root__" });
	const navigate = useNavigate();
	const [queryEnabled, setQueryEnabled] = useState(false);

	const storedSession = getBankLinkingSessionFromStorage();
	const { user } = useAuth();

	useEffect(() => {
		const timer = setTimeout(() => setQueryEnabled(true), QUERY_DELAY_MS);
		return () => clearTimeout(timer);
	}, []);

	const statusQuery = useGetBankLinkingSessionStatus(
		{
			guestId: user.id,
			sessionId: storedSession?.session_id ?? "",
			locationId: location_id,
		},
		{ enabled: queryEnabled },
	);

	const isRefetching = statusQuery.isRefetching;

	// Clear session from storage when we have a terminal result
	useEffect(() => {
		const result = statusQuery.data?.result;
		if (result === "success" || result === "failed" || result === "not_found") {
			clearBankLinkingSessionFromStorage();
		}
	}, [statusQuery.data?.result]);

	const renderContent = () => {
		// Show loading spinner while fetching status (initial load or refetch with no data yet)
		if (statusQuery.isLoading || !queryEnabled) {
			return (
				<div className="flex flex-col items-center text-center space-y-4">
					<Spinner className="h-10 w-10" />
					<Typography variant="h3" className="font-semibold">
						Checking your bank connection...
					</Typography>
					<Typography variant="p" className="text-sm text-textLight max-w-sm">
						Please wait while we verify your bank connection status.
					</Typography>
				</div>
			);
		}

		const { status } = statusQuery;

		if (status === "pending") {
			return (
				<div className="flex flex-col items-center text-center space-y-4">
					<Typography variant="h3" className="font-semibold">
						Your bank connection is currently being validated
					</Typography>
					<Typography variant="p" className="text-sm text-textLight max-w-sm">
						Your bank connection is currently being validated. This can take a
						few minutes. You can refresh the status below.
					</Typography>
					<div className="flex flex-wrap gap-3 justify-center mt-4">
						<Button
							variant="outline"
							onClick={() => statusQuery.refetch()}
							disabled={isRefetching}
						>
							{isRefetching ? "Refreshing..." : "Refresh status"}
						</Button>
					</div>
				</div>
			);
		}

		if (status === "error") {
			return (
				<div className="flex flex-col items-center text-center space-y-4">
					<Typography variant="h3" className="font-semibold">
						We couldn&apos;t verify your bank connection
					</Typography>
					<Typography variant="p" className="text-sm text-textLight max-w-sm">
						Something went wrong while checking your bank connection status.
						Please try again in a moment or start a new connection.
					</Typography>
					<div className="flex gap-3 mt-2">
						<Button
							variant="outline"
							onClick={() => navigate({ to: "/main/setting/linked-accounts" })}
						>
							Back to linked accounts
						</Button>
						<Button
							onClick={() => statusQuery.refetch()}
							disabled={statusQuery.isFetching}
						>
							Try again
						</Button>
					</div>
				</div>
			);
		}

		// At this point, we have successful query data with a typed result:
		// "pending" | "success" | "failed" | "not_found"
		switch (statusQuery.data.result) {
			case "pending":
				return (
					<div className="flex flex-col items-center text-center space-y-4">
						<Typography variant="h3" className="font-semibold">
							Your bank connection is currently being validated
						</Typography>
						<Typography variant="p" className="text-sm text-textLight max-w-sm">
							Your bank connection is currently being validated. This can take a
							few minutes. You can refresh the status below.
						</Typography>
						<div className="flex flex-wrap gap-3 justify-center mt-4">
							<Button
								variant="outline"
								onClick={() => statusQuery.refetch()}
								disabled={isRefetching}
							>
								{isRefetching ? "Refreshing..." : "Refresh status"}
							</Button>
						</div>
					</div>
				);

			case "success":
				return (
					<div className="flex flex-col items-center text-center space-y-5">
						<CheckCircle2 className="h-10 w-10 text-primary-600" />
						<div className="space-y-2">
							<Typography variant="h3" className="font-semibold">
								Your bank connection is ready
							</Typography>
						</div>
						<div className="flex flex-wrap gap-3 justify-center">
							<Button
								onClick={() =>
									navigate({ to: "/main/setting/linked-accounts" })
								}
							>
								Back to linked accounts
							</Button>
						</div>
					</div>
				);

			case "failed": {
				const errorResponse = BankLinkingFailedResponseSchema.safeParse(
					statusQuery.data,
				);
				const errorMessage = errorResponse.success
					? errorResponse.data.error_message
					: static_errors.unexpected_contact_support;

				return (
					<div className="flex flex-col items-center text-center space-y-5">
						<AlertTriangle className="h-10 w-10 text-primary-600" />
						<div className="space-y-2">
							<Typography variant="h3" className="font-semibold">
								There was a problem with your bank connection
							</Typography>
							<Typography variant="p" className="text-sm mx-auto text-textLight max-w-sm text-center">
								{errorMessage}
							</Typography>
						</div>
						<div className="flex flex-wrap gap-3 justify-center">
							<Button
								onClick={() =>
									navigate({ to: "/main/setting/linked-accounts/new" })
								}
							>
								Start a new connection
							</Button>
						</div>
					</div>
				);
			}

			case "not_found":
				return (
					<div className="flex flex-col items-center text-center space-y-4">
						<Typography variant="h3" className="font-semibold">
							We couldn&apos;t find a bank connection in progress
						</Typography>
						<Typography variant="p" className="text-sm text-textLight max-w-sm">
							It looks like this verification link has expired or the session
							has already been completed. You can start a new bank connection
							from the Linked Accounts page.
						</Typography>
						<div className="flex gap-3 mt-2">
							<Button
								variant="outline"
								onClick={() =>
									navigate({ to: "/main/setting/linked-accounts" })
								}
							>
								Back to linked accounts
							</Button>
						</div>
					</div>
				);
		}
	};

	return (
		<PageTransition>
			<div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 bg-background text-textDark">
				<div className="w-full max-w-lg rounded-2xl border border-border bg-white px-6 py-10 shadow-sm">
					{renderContent()}
				</div>
			</div>
		</PageTransition>
	);
};

export default VerifyBankLinkingPage;
